import React, { useEffect, useMemo, useState } from 'react';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { useRouter } from 'next/router';
import { useDIContext } from '@/Framework/DI/DIContext';
import ContactRepository from '@/finsight/infrastructure/repository/ContactRepository';
import finsightWebUrl from '@/finsight/infrastructure/finsightWebUrl';
import usersUrl from '@/users/infrastructure/usersUrl';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';
import getPlansConfig, { basePlansConfig } from './plansConfig';
import PlanPanel from './PlanPanel';
import LoginModal from '../LoginModal';
import { ActionType } from '@/finsight/domain/Pricing/ActionType';
import UserPlan, { UserPlanType } from '@/users/domain/UserPlan';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { useSessionContext } from '@/users/application/Session/SessionContext';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import config from '@finsight/config';

import styles from '../styles.scss';

const PlanPanelsBlock = () => {
  const {
    replace,
    asPath,
  } = useRouter();
  const tenant = config.code;
  const { container } = useDIContext();
  const contactRepository = container.get(ContactRepository);
  const {
    plan,
    requests,
    getUserPlan,
    getUserRequests,
  } = useUserPlanContext();
  const { currentUser } = useSessionContext();
  const [isVisibleModal, setIsVisibleModal] = useState<boolean>(false);
  const [selectPlan, setSelectPlan] = useState<UserPlanType>(UserPlanType.UNAUTHORIZED);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  useEffect(() => {
    if (UserPlan.isUnauthorized(plan)) return;
    getUserRequests();
  }, []);

  const onOpenModal = (plan: UserPlanType) => {
    setSelectPlan(plan);
    setIsVisibleModal(true);
  };

  const onCloseModal = () => {
    setSelectPlan(UserPlanType.UNAUTHORIZED);
    setIsVisibleModal(false)
  };

  const onLogin = () => {
    const callbackUrl = finsightWebUrl.getPropagateCallbackUrl(asPath);
    const url = usersUrl.getLoginUrl(callbackUrl, tenant);

    replace(url);
  };

  const getSuccessRequestMessage = (plan: UserPlanType, userEmail: string) => {
    const planConfig = basePlansConfig.find(item => item.plan === plan);
    const title = startCase(toLower(planConfig.title));
    return `Your request to update your Finsight.com Subscription to the ${ title } Plan has been submitted and will be reviewed shortly. `
      + `We will email updates to ${ userEmail }.`
  };

  const onRequestPlan = async (plan: UserPlanType) => {
    try {
      setIsFetching(true);
      await contactRepository.subscriptionRequest({ plan });
      await getUserPlan();
      await getUserRequests();
      AlertManager.success(getSuccessRequestMessage(plan, currentUser.email));
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsFetching(false);
    }
  };

  const plansConfig = useMemo(() => {
    const config = getPlansConfig(plan, requests);

    config.forEach((item) => {
      if (item.actionType === ActionType.LOGIN_AND_REQUEST) {
        item.onClickButton = onOpenModal;
      } else if (item.actionType === ActionType.LOGIN) {
        item.onClickButton = onLogin;
      } else if (item.actionType === ActionType.REQUEST) {
        item.onClickButton = onRequestPlan;
      }
    });

    return config;
  }, [plan, requests]);

  return (
    <>
      <Spinner isVisible={ isFetching } overlay />
      <div className={ styles.pricingPlansPanelBody }>
        { plansConfig.map((plan) => <PlanPanel key={ plan.dataTest } { ...plan } />) }
      </div>
      <LoginModal isVisible={ isVisibleModal } onClose={ onCloseModal } plan={ selectPlan } />
    </>
  );
}

export default PlanPanelsBlock;
